import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error(t("auth.errors.emailInvalid"));
      return;
    }
    setSubmitting(true);
    try {
      // Le link envoyé pointera vers /auth/reset-password — Supabase y
      // attache un access_token dans le hash, qu'on récupère côté reset
      // pour appeler updateUser({ password }).
      const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (e) {
      // On affiche toujours le même message succès même si erreur — pour
      // ne pas révéler si un email existe ou non (anti-énumération).
      // eslint-disable-next-line no-console
      console.warn("resetPasswordForEmail error", e);
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col safe-top safe-bottom px-6 py-8">
      <header className="flex items-center gap-2 mb-8">
        <Button asChild variant="ghost" size="icon-sm">
          <Link to="/welcome"><ArrowLeft className="size-[18px]" /></Link>
        </Button>
        <Logo size="sm" />
      </header>

      <div className="flex-1 max-w-md w-full mx-auto">
        <h1 className="text-2xl font-bold tracking-tight">{t("auth.forgotPasswordTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.forgotPasswordHint")}</p>

        {sent ? (
          <div className="mt-8 rounded-xl border border-success/30 bg-success/[0.06] px-4 py-4">
            <div className="flex items-start gap-3">
              <Mail className="size-4 text-success mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">{t("auth.resetSent")}</p>
                <p className="text-2xs text-muted-foreground mt-1">
                  Pense à vérifier tes spams.
                </p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="w-full mt-4">
              <Link to="/welcome">{t("auth.signIn")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="toi@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" size="lg" className="w-full" loading={submitting}>
              {t("auth.sendResetLink")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
